import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { HeroSlide } from "@/mocks/site";

interface HeroCarouselProps {
  slides: HeroSlide[];
  onSelect: (slide: HeroSlide) => void;
}

export default function HeroCarousel({ slides, onSelect }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [paused, slides.length]);

  const go = (next: number) => setIndex((next + slides.length) % slides.length);
  const active = slides[index];

  return (
    <section
      className="relative h-[560px] w-full overflow-hidden bg-foreground-900 md:h-[680px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点资讯轮播"
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} - 佐佐木明希焦点资讯配图`}
            title={`${slide.title} 佐佐木明希`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/25" />

      <div className="relative mx-auto flex h-full w-full max-w-[1200px] flex-col justify-end px-4 pb-16 md:px-6 md:pb-20">
        <div key={active.id} className="animate-fade-up max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-3 py-1 text-xs font-label uppercase tracking-[0.2em] text-background-50 backdrop-blur">
            <i className="ri-flashlight-fill text-accent-300" aria-hidden="true" />
            Featured
          </span>
          <h2 className="mt-5 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {active.title}
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-50/85 md:text-base">
            {active.subtitle}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => onSelect(active)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看详情
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </button>
            <Link
              to="/works"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              浏览全部作品
            </Link>
          </div>
        </div>

        <div className="mt-10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => go(i)}
                aria-label={`切换到第 ${i + 1} 张焦点图`}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? "w-8 bg-background-50" : "w-3 bg-background-50/45 hover:bg-background-50/70"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => go(index - 1)}
              aria-label="上一张"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-arrow-left-s-line text-xl" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              aria-label="下一张"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-arrow-right-s-line text-xl" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}